import { Head, router, usePage } from "@inertiajs/react"
import { MoreHorizontal, Plus, Shield } from "lucide-react"
import { useState } from "react"

import { Avatar, Kpi, KpiStrip, PageHeader, Section, StatusBadge } from "@/components/steel"
import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  CredentialsDialog,
  UserPasswordDialog,
  type DialogMode,
} from "@/pages/Users/UserPasswordDialog"
import type { SharedProps } from "@/types"

type UserRow = {
  id: number
  name: string
  email: string
  initials: string
  status: string
  isYou: boolean
  lastSignIn: string | null
  createdAt: string
}

type Props = {
  suggestedPassword: string
  users: UserRow[]
  stats: { total: number; pending: number; signedInWeek: number }
}

function formatDate(iso: string | null) {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
}

function formatSignIn(iso: string | null) {
  if (!iso) return "Never"
  const date = new Date(iso)
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000)
  if (days === 0) return `Today, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
  if (days === 1) return "Yesterday"
  return formatDate(iso)
}

export default function UsersIndex({ stats, suggestedPassword, users }: Props) {
  const { flash } = usePage<SharedProps>().props
  const [dialog, setDialog] = useState<DialogMode | null>(null)

  const settled = stats.total - stats.pending

  return (
    <>
      <Head title="Users" />

      <PageHeader
        title="Users"
        meta="Who can sign in to SteelTrack"
        actions={
          <Button size="sm" onClick={() => setDialog({ kind: "add" })}>
            <Plus />
            Add user
          </Button>
        }
      />

      <KpiStrip>
        <Kpi label="Users" value={stats.total} hint="Everyone has admin access" />
        <Kpi
          label="Temporary passwords"
          value={stats.pending}
          hint={stats.pending ? "Not replaced yet" : "None outstanding"}
        />
        <Kpi
          label="Signed in this week"
          value={stats.signedInWeek}
          hint={`Of ${settled} settled ${settled === 1 ? "account" : "accounts"}`}
        />
      </KpiStrip>

      <Section className="pb-7">
        <DataTable>
          <Thead>
            <Th>User</Th>
            <Th>Access</Th>
            <Th>Status</Th>
            <Th>Last sign-in</Th>
            <Th>Added</Th>
            <Th />
          </Thead>
          <tbody>
            {users.map((u) => (
              <Tr key={u.id}>
                <Td className="py-2.5">
                  <span className="flex items-center gap-2.5">
                    <Avatar initials={u.initials} size={28} />
                    <span className="min-w-0">
                      <span className="block text-body font-medium">{u.name}</span>
                      <span className="block text-label text-muted-foreground">{u.email}</span>
                    </span>
                  </span>
                </Td>
                <Td>
                  <span className="inline-flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                    Admin
                  </span>
                </Td>
                <Td>
                  <StatusBadge
                    status={u.status}
                    label={u.status === "pending" ? "Temporary password" : undefined}
                  />
                </Td>
                <Td muted num>
                  {formatSignIn(u.lastSignIn)}
                </Td>
                <Td muted num>
                  {formatDate(u.createdAt)}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  {u.isYou ? (
                    <span className="text-label text-muted-foreground">You</span>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            setDialog({ kind: "reset", user: { id: u.id, name: u.name, email: u.email } })
                          }
                        >
                          Reset password
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => router.delete(`/users/${u.id}`)}
                        >
                          Remove access
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>

        <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-border bg-card px-3.5 py-3">
          <Shield className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
          <p className="text-label leading-relaxed text-muted-foreground">
            Everyone who can sign in has full access today. When you need narrower access — field crews
            logging time, a bookkeeper who only sees invoices — roles get added here.
          </p>
        </div>
      </Section>

      {flash.credentials ? (
        <CredentialsDialog
          credentials={flash.credentials}
          onAddAnother={() => router.reload({ onSuccess: () => setDialog({ kind: "add" }) })}
          onClose={() => router.reload()}
        />
      ) : dialog ? (
        <UserPasswordDialog
          mode={dialog}
          onClose={() => setDialog(null)}
          suggestedPassword={suggestedPassword}
        />
      ) : null}
    </>
  )
}
